import clsx from "clsx";
import { Button } from "../Button";
import React from "react";

export enum NotificationSize {
  small = "small",
  medium = "medium",
  large = "large",
}

export enum NotificationIcon {
  warning = "icon-lt-alert-line",
  info = "icon-lt-information-line",
  check = "icon-lt-check-line",
  question = "icon-lt-question-line",
  error = "icon-lt-error-warning-line",
}
enum NotificationType {
  dark,
  success,
  error,
  info,
  warning,
}

type NotificationAction = {
  text: string;
  onClick: () => void;
};

export type NotificationProps = {
  title: string;
  message?: string | React.ReactNode;
  icon?: keyof typeof NotificationIcon;
  onClickClose?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  size?: keyof typeof NotificationSize;
  closable?: boolean;
  className?: string;
  additionalClassName?: string;
  type?: keyof typeof NotificationType;
  primaryAction?: NotificationAction;
  secondaryAction?: NotificationAction;
};

const sharedClassnames =
  "pl-3 pr-2 flex  min-w-[40%] max-w-[600px] text-center text-sm font-medium leading-5 border border-solid rounded-lg";

const Light = ({
  title,
  message,
  icon,
  onClickClose,
  size = "small",
  closable,
  className,
  additionalClassName,
  type,
  primaryAction,
  secondaryAction,
}: NotificationProps) => {
  const containerClass = clsx(
    sharedClassnames,
    additionalClassName,
    {
      ["py-2 min-h-[56px] atext-xs leading-3"]: size === "small",
      ["py-4 min-h-[72px] atext-sm leading-3"]: size === "medium",
      ["py-4 min-h-[72px] atext-base leading-3 border-l-8"]: size === "large",
    },
    className
  );

  const textClass = clsx("m-0 text-sm font-semibold leading-4", {
    ["leading-3"]: size === "small",
    ["leading-4"]: size === "medium" || size === "large",
  });

  const iconClass = clsx(
    "pr-1 text-sm font-medium leading-4",
    {
      ["leading-3"]: size === "small",
      ["leading-4"]: size === "medium" || size === "large",
    },
    {
      "text-green-800": type === "success",
      "text-white": type === "dark",
      "text-red-700": type === "error",
      "text-blue-700": type === "info",
      "text-amber-600": type === "warning",
    }
  );

  return (
    <div className={containerClass} data-testid="li-react-notification">
      <div className="flex flex-row">
        {icon && (
          <div className={iconClass}>
            <i className={NotificationIcon[icon]} />
          </div>
        )}
        <div className="flex flex-col grow">
          <div className="flex flex-row justify-between grow text-left">
            <p className={textClass}>{title}</p>
          </div>
          {message && <p className="m-0 mr-5 pt-1 leading-4 font-normal text-start">{message}</p>}
        </div>
        {primaryAction && <Button onClick={primaryAction.onClick}>{primaryAction.text}</Button>}
        {secondaryAction && (
          <Button variant="secondary" onClick={secondaryAction.onClick} className="text-neutral-800">
            {secondaryAction.text}
          </Button>
        )}
        {closable && onClickClose && (
          <div>
            <button className="flex flex-wrap content-center" onClick={onClickClose}>
              <i aria-label="Close" role="img" className="icon-lt-close-line" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Error = ({ ...rest }: NotificationProps) => {
  const errorClass = "bg-red-50 text-neutral-800 border-red-700";
  return <Light className={errorClass} type="error" {...rest} />;
};

const Dark = ({ ...rest }: NotificationProps) => {
  const darkClass = "bg-neutral-700 text-white border-neutral-800";
  return <Light className={darkClass} type="dark" {...rest} />;
};

const Success = ({ ...rest }: NotificationProps) => {
  const successClass = "bg-green-50 text-neutral-800 border-green-800";
  return <Light className={successClass} type="success" {...rest} />;
};

const Warning = ({ ...rest }: NotificationProps) => {
  const warningClass = "bg-amber-50 text-neutral-800 border-amber-500";
  return <Light className={warningClass} type="warning" {...rest} />;
};

const Info = ({ ...rest }: NotificationProps) => {
  const infoClass = "bg-blue-100 text-neutral-800 border-platform-500";
  return <Light className={infoClass} type="info" {...rest} />;
};

export const Notification = {
  Info,
  Warning,
  Error,
  Light,
  Dark,
  Success,
};
